import { CheckResult } from '@backstage-community/plugin-tech-insights-common';

/** An option in one of the sidebar's dropdowns. */
export type FilterOption = {
  /** What the selection stores, and what the row is matched against. */
  value: string;
  /** What the dropdown shows. */
  label: string;
};

/**
 * How an entity is doing overall. An entity has exactly one of these, so a
 * filter over them cannot ask for a combination that nothing can satisfy.
 */
export type ScorecardStatus =
  | 'failing'
  | 'undetermined'
  | 'passing'
  | 'unchecked';

/** One entity, its checks, and the catalog metadata the filters need. */
export type ScorecardRow = {
  /** Stringified entity ref, as the bulk check endpoint returns it. */
  entityRef: string;
  /** How the entity is named in the Entity dropdown. */
  label: string;
  /**
   * Entity kind, spelled as the catalog spells it, so `API` not `Api`. An
   * entity the catalog did not return is known only by its ref, and
   * `parseEntityRef` lowercases the kind, so the same kind can arrive here
   * under either spelling. The dropdown collapses them.
   */
  kind: string;
  /** Groups owning the entity. Empty when the entity has no owner. */
  owners: FilterOption[];
  results: CheckResult[];
};

/** What the sidebar has selected. An empty list means "do not narrow on this". */
export type ScorecardSelection = {
  statuses: string[];
  owners: string[];
  kinds: string[];
  entityRefs: string[];
  failingCheckIds: string[];
  /**
   * Which checks to run at all. Unlike the others this reaches the backend, so
   * changing it refetches rather than refilters.
   */
  checkIds: string[];
};

/**
 * Whether a check result failed, and whether it could be evaluated at all.
 * Injected so the filters stay independent of the API and its renderers.
 */
export type CheckResultPredicates = {
  isFailed: (result: CheckResult) => boolean;
  isUndetermined: (result: CheckResult) => boolean;
};

/** The four statuses, in the order the sidebar offers them. */
export const statusOptions: FilterOption[] = [
  { value: 'failing', label: 'Failing' },
  { value: 'undetermined', label: 'Not determined' },
  { value: 'passing', label: 'Passing' },
  { value: 'unchecked', label: 'Not checked' },
];

/** Lands on the entities that need work rather than on the ones that do not. */
export const defaultSelection: ScorecardSelection = {
  statuses: ['failing'],
  owners: [],
  kinds: [],
  entityRefs: [],
  failingCheckIds: [],
  checkIds: [],
};

/**
 * The entity's overall status.
 *
 * A failure outranks a check that could not be evaluated, which outranks a
 * pass, so an entity with one of each reads as failing. "Not determined" is a
 * status of its own because a check the backend could not run says nothing
 * about the entity, and lumping it in with the passes overstates how green the
 * estate is.
 */
export const statusOfRow = (
  row: ScorecardRow,
  predicates: CheckResultPredicates,
): ScorecardStatus => {
  if (row.results.length === 0) {
    return 'unchecked';
  }
  if (row.results.some(result => predicates.isFailed(result))) {
    return 'failing';
  }
  if (row.results.some(result => predicates.isUndetermined(result))) {
    return 'undetermined';
  }
  return 'passing';
};

const matchesAny = (selected: string[], values: string[]) =>
  selected.length === 0 || values.some(value => selected.includes(value));

/**
 * Applies every sidebar filter.
 *
 * Each one only ever narrows, and an empty selection narrows nothing, so no
 * combination can return an empty table for a reason the sidebar does not show.
 */
export const filterScorecardRows = (
  rows: ScorecardRow[],
  selection: ScorecardSelection,
  predicates: CheckResultPredicates,
): ScorecardRow[] =>
  rows.filter(
    row =>
      matchesAny(selection.statuses, [statusOfRow(row, predicates)]) &&
      matchesAny(
        selection.owners,
        row.owners.map(owner => owner.value),
      ) &&
      matchesAny(selection.kinds, [row.kind.toLowerCase()]) &&
      matchesAny(selection.entityRefs, [row.entityRef]) &&
      (selection.failingCheckIds.length === 0 ||
        row.results.some(
          result =>
            predicates.isFailed(result) &&
            selection.failingCheckIds.includes(result.check.id),
        )),
  );

const byLabel = (a: FilterOption, b: FilterOption) =>
  a.label.localeCompare(b.label);

const dedupe = (options: FilterOption[]) => [
  ...new Map(options.map(option => [option.value, option])).values(),
];

/**
 * The kinds on the page, one option each.
 *
 * Keyed by the lowercased kind rather than by the kind itself, because a row
 * for an entity the catalog did not return carries the ref's spelling, `api`
 * where a row that has its entity carries `API`. Keying on the kind as written
 * would offer that kind twice, and picking one would hide the rows spelled the
 * other way.
 *
 * The label prefers whichever spelling the catalog gave, so the dropdown reads
 * `API` rather than `api` as long as one row on the page has its entity.
 */
const dedupeKinds = (rows: ScorecardRow[]) => [
  ...rows
    .reduce((kinds, row) => {
      const value = row.kind.toLowerCase();
      const chosen = kinds.get(value);
      if (!chosen || chosen.label === value) {
        kinds.set(value, { value, label: row.kind });
      }
      return kinds;
    }, new Map<string, FilterOption>())
    .values(),
];

/**
 * The options each dropdown offers, taken from the rows that came back rather
 * than from a fixed list, so the sidebar only offers what the table can show.
 *
 * Derive these from the unfiltered rows. Deriving them from the filtered ones
 * would make the options vanish as they are used.
 */
export const filterOptionsFromRows = (
  rows: ScorecardRow[],
  predicates: CheckResultPredicates,
) => ({
  owners: dedupe(rows.flatMap(row => row.owners)).sort(byLabel),
  kinds: dedupeKinds(rows).sort(byLabel),
  entities: dedupe(
    rows.map(row => ({ value: row.entityRef, label: row.label })),
  ).sort(byLabel),
  failingChecks: dedupe(
    rows.flatMap(row =>
      row.results
        .filter(result => predicates.isFailed(result))
        .map(result => ({ value: result.check.id, label: result.check.name })),
    ),
  ).sort(byLabel),
});

/**
 * Orders entities by how much attention they need: most failing checks first,
 * then most checks run.
 */
export const sortByCheckResults =
  (predicates: CheckResultPredicates) => (a: ScorecardRow, b: ScorecardRow) => {
    const aFailed = a.results.filter(r => predicates.isFailed(r)).length;
    const bFailed = b.results.filter(r => predicates.isFailed(r)).length;

    if (aFailed !== bFailed) {
      return bFailed - aFailed;
    }
    return b.results.length - a.results.length;
  };
